import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Button, Container, Stack, Typography } from '@mui/material';
// components
import Logo from '../../components/Logo';
import AuthBrandPanel from './AuthBrandPanel';

// ----------------------------------------------------------------------

const RootStyle = styled('main')(({ theme }) => ({
  display: 'flex',
  minHeight: '100dvh',
  backgroundColor: theme.palette.background.default,
}));

const ContentPane = styled('section')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flex: '1 1 auto',
  minWidth: 0,
  backgroundColor: theme.palette.background.paper,
}));

const MobileHeader = styled('header')(({ theme }) => ({
  top: 0,
  left: 0,
  zIndex: 1,
  width: '100%',
  position: 'absolute',
  padding: theme.spacing(3, 3, 0),
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const ContentStyle = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 480,
  margin: 'auto',
  padding: theme.spacing(14, 0, 6),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(13, 0, 4),
  },
}));

const AccountSwitch = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[500_16]}`,
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.primary.main, 0.035),
}));

// ----------------------------------------------------------------------

AuthLayout.propTypes = {
  actionLabel: PropTypes.string.isRequired,
  actionTo: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  description: PropTypes.string.isRequired,
  prompt: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

export default function AuthLayout({ children, title, description, prompt, actionLabel, actionTo }) {
  return (
    <RootStyle>
      <AuthBrandPanel />

      <ContentPane>
        <MobileHeader>
          <Logo />
        </MobileHeader>

        <Container maxWidth="sm" sx={{ display: 'flex' }}>
          <ContentStyle data-testid="auth-surface">
            <Stack data-ph-capture="safe" spacing={1} sx={{ mb: 4 }}>
              <Typography component="h1" variant="h3" sx={{ color: 'text.primary', letterSpacing: -0.4 }}>
                {title}
              </Typography>
              <Typography sx={{ color: 'text.secondary', maxWidth: 400 }}>
                {description}
              </Typography>
            </Stack>

            {children}

            <AccountSwitch data-ph-capture="safe">
              <Stack spacing={1.25} alignItems="center">
                <Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>
                  {prompt}
                </Typography>
                <Button
                  fullWidth
                  size="large"
                  color="primary"
                  variant="outlined"
                  component={RouterLink}
                  to={actionTo}
                  sx={{ bgcolor: 'background.paper' }}
                >
                  {actionLabel}
                </Button>
              </Stack>
            </AccountSwitch>
          </ContentStyle>
        </Container>
      </ContentPane>
    </RootStyle>
  );
}
